/**
 * 音声解析専用画面
 * チェックシート生成をスキップして音声解析機能のみを使用
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Grid,
  Container,
  CircularProgress,
  Alert,
  IconButton,
  Divider,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
} from '@mui/material';
import { motion } from 'framer-motion';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MicIcon from '@mui/icons-material/Mic';
import PersonIcon from '@mui/icons-material/Person';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import HistoryIcon from '@mui/icons-material/History';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import AudioRecorderPanel from './components/AudioRecorder/AudioRecorderPanel';
import { audioOnlyService, audioService } from './services/audioAPI';
import { designTokens } from '../../theme/designTokens';

const theme = designTokens.featureThemes.interview_support;

function AudioOnlyScreen({ onBack }) {
  // フォーム状態
  const [candidateName, setCandidateName] = useState('');
  const [interviewerName, setInterviewerName] = useState('');
  const [position, setPosition] = useState('');
  const [formErrors, setFormErrors] = useState({});

  // セッション状態
  const [sessionCreating, setSessionCreating] = useState(false);
  const [sessionError, setSessionError] = useState(null);
  const [currentSession, setCurrentSession] = useState(null);

  // 過去のセッション一覧
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  // 過去のセッション一覧を取得
  const fetchSessions = async () => {
    try {
      setSessionsLoading(true);
      const data = await audioOnlyService.getSessions();
      setSessions(data);
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
    } finally {
      setSessionsLoading(false);
    }
  };

  // 初回マウント時にセッション一覧を取得
  useEffect(() => {
    fetchSessions();
  }, []);

  // フォームバリデーション
  const validateForm = () => {
    const errors = {};
    if (!candidateName.trim()) {
      errors.candidateName = '応募者名を入力してください';
    }
    if (!interviewerName.trim()) {
      errors.interviewerName = '面接官名を入力してください';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // セッション作成
  const handleCreateSession = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setSessionCreating(true);
      setSessionError(null);

      const session = await audioOnlyService.createSession(
        candidateName.trim(),
        interviewerName.trim(),
        position.trim() || null
      );

      setCurrentSession(session);
    } catch (error) {
      console.error('Failed to create session:', error);
      setSessionError(
        error.response?.data?.detail || 'セッションの作成に失敗しました'
      );
    } finally {
      setSessionCreating(false);
    }
  };

  // 過去のセッションを選択
  const handleSelectSession = async (session) => {
    try {
      const detail = await audioOnlyService.getSession(session.id);
      setCurrentSession(detail);
      setShowHistory(false);
    } catch (error) {
      console.error('Failed to get session:', error);
    }
  };

  // セッション画面から戻る
  const handleBackFromSession = () => {
    setCurrentSession(null);
    setCandidateName('');
    setInterviewerName('');
    setPosition('');
    setFormErrors({});
    fetchSessions();
  };

  // 入力フォーム画面
  const renderInputForm = () => (
    <Container maxWidth="md" sx={{ py: 6 }}>
      <Box
        component={motion.div}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
      >
        {/* ヘッダー */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
          <IconButton onClick={onBack} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography
              variant="h4"
              sx={{ fontWeight: 700, color: theme.primary }}
            >
              音声解析のみ
            </Typography>
            <Typography variant="body1" color="text.secondary">
              チェックシート生成をスキップして音声解析機能を使用
            </Typography>
          </Box>
        </Box>

        {/* 過去のセッション履歴トグル */}
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            startIcon={<HistoryIcon />}
            onClick={() => setShowHistory(!showHistory)}
            sx={{ borderRadius: 2 }}
          >
            {showHistory ? '新規作成に戻る' : '過去のセッション'}
          </Button>
        </Box>

        {showHistory ? (
          // 過去のセッション一覧
          <Paper
            elevation={0}
            sx={{
              p: 4,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              過去の音声解析セッション
            </Typography>

            {sessionsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={32} />
              </Box>
            ) : sessions.length === 0 ? (
              <Alert severity="info">
                過去のセッションはありません
              </Alert>
            ) : (
              <List>
                {sessions.map((session) => (
                  <ListItem
                    key={session.id}
                    button
                    onClick={() => handleSelectSession(session)}
                    sx={{
                      borderRadius: 2,
                      mb: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      '&:hover': {
                        backgroundColor: `${theme.primary}10`,
                        borderColor: theme.primary,
                      },
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {session.candidate_name}
                          </Typography>
                          {session.position && (
                            <Chip
                              label={session.position}
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <>
                          <Typography variant="body2" color="text.secondary">
                            面接官: {session.interviewer_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            作成日: {new Date(session.created_at).toLocaleDateString()}
                            {' | '}
                            録音数: {session.recording_count || 0}件
                          </Typography>
                        </>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<PlayArrowIcon />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSelectSession(session);
                        }}
                        sx={{ borderRadius: 2, textTransform: 'none' }}
                      >
                        開く
                      </Button>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        ) : (
          // 新規作成フォーム
          <Paper
            elevation={0}
            sx={{
              p: 4,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  backgroundColor: `${theme.primary}15`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <MicIcon sx={{ color: theme.primary, fontSize: 24 }} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  新しい音声解析セッション
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  以下の情報を入力して音声解析を開始
                </Typography>
              </Box>
            </Box>

            {sessionError && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {sessionError}
              </Alert>
            )}

            <Grid container spacing={3}>
              {/* 応募者名 */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label="応募者名"
                  value={candidateName}
                  onChange={(e) => setCandidateName(e.target.value)}
                  error={!!formErrors.candidateName}
                  helperText={formErrors.candidateName}
                  InputProps={{
                    startAdornment: (
                      <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    ),
                  }}
                />
              </Grid>

              {/* 面接官名 */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label="面接官名"
                  value={interviewerName}
                  onChange={(e) => setInterviewerName(e.target.value)}
                  error={!!formErrors.interviewerName}
                  helperText={formErrors.interviewerName}
                  InputProps={{
                    startAdornment: (
                      <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    ),
                  }}
                />
              </Grid>

              {/* 職種（任意） */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="職種（任意）"
                  value={position}
                  onChange={(e) => setPosition(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <BusinessCenterIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    ),
                  }}
                  helperText="スコア重み付けの参考に使用されます"
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 4 }} />

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={onBack}
                sx={{ borderRadius: 2 }}
              >
                キャンセル
              </Button>
              <Button
                variant="contained"
                onClick={handleCreateSession}
                disabled={sessionCreating}
                startIcon={
                  sessionCreating ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <MicIcon />
                  )
                }
                sx={{
                  borderRadius: 2,
                  px: 4,
                  background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.light} 100%)`,
                }}
              >
                音声解析を開始
              </Button>
            </Box>
          </Paper>
        )}
      </Box>
    </Container>
  );

  // 音声解析パネル画面
  const renderAudioPanel = () => (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box
        component={motion.div}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
      >
        {/* ヘッダー */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
          <IconButton onClick={handleBackFromSession} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1 }}>
            <Typography
              variant="h5"
              sx={{ fontWeight: 700, color: theme.primary }}
            >
              音声解析
            </Typography>
            <Typography variant="body2" color="text.secondary">
              応募者: {currentSession.candidate_name}
              {currentSession.position && ` | 職種: ${currentSession.position}`}
              {' | '}面接官: {currentSession.interviewer_name}
            </Typography>
          </Box>
          <Chip
            label="音声解析のみ"
            color="primary"
            variant="outlined"
            icon={<MicIcon />}
          />
        </Box>

        {/* 音声録音パネル */}
        <AudioRecorderPanel
          checkSheetId={currentSession.id}
          audioService={audioService}
          onRecordingComplete={(recording) => {
            console.log('Recording complete:', recording);
          }}
          onUploadComplete={(recording) => {
            console.log('Upload complete:', recording);
          }}
          onAnalysisComplete={(results) => {
            console.log('Analysis complete:', results);
          }}
        />
      </Box>
    </Container>
  );

  // 現在のセッションがある場合は音声パネル、なければ入力フォーム
  return currentSession ? renderAudioPanel() : renderInputForm();
}

export default AudioOnlyScreen;
